import {BookingStep} from "@page-containers/booking/utils/bookingStep";
import {debounce} from "lodash";
import {CustomCategory, EventType} from "src/utils/analytics/constants";

import {GA} from "../../../../_services/api";
import {ApptSlot} from "../../../../_services/monolith/availability";
import {PracticeNameById} from "../../../../constants/practiceIds";
import {ApptDetails, getEventQueuer, queueEvent} from "../../../../utils/analytics/eventQueue";
import {getFlowId} from "../../../../utils/getFlowId";

const postEvent = getEventQueuer({
  extraData: {
    category: CustomCategory.AppointmentScheduling,
  },
});

export const emitChangeDateEvent = (value: string, slotOptionsId: string, viewId: string) => {
  postEvent({
    typeId: EventType.ChangeDateClicked,
    extraData: {
      slotOptionsId,
      value,
      viewId,
      flowId: getFlowId() || null,
    },
  });
};

const formatSlotOptions = (slot: ApptSlot, index: number) => ({
  doctorId: slot.doctorId,
  rank: index + 1,
  slotId: slot.id,
});

export const emitSlotOptionsViewedEvent = debounce(
  ({
    slotOptionsId,
    soonestSlotTime,
    numberOfSlotsInADay,
    emptyListText,
    options = [],
    nearbyOptions = [],
    openMinutes,
    viewId,
    limitedAvailabilityFlag,
  }: {
    slotOptionsId: string;
    soonestSlotTime: number | null;
    numberOfSlotsInADay: number;
    emptyListText: string | null;
    options?: ApptSlot[];
    nearbyOptions?: ApptSlot[];
    openMinutes?: number | null;
    viewId?: string;
    limitedAvailabilityFlag?: boolean;
  }) => {
    postEvent({
      typeId: EventType.SlotOptionsViewed,
      extraData: {
        limitedAvailabilityFlag,
        slotOptionsId,
        soonestSlotTime,
        openMinutes,
        numberOfSlotsInADay,
        emptyListText,
        options: options.map(formatSlotOptions),
        nearbyOptions: nearbyOptions.map(formatSlotOptions),
        viewId,
        flowId: getFlowId() || null,
      },
    });
  },
  500,
);

enum ModalityState {
  DEFAULT = "default",
  CLICKED = "clicked",
  UNAVAILABLE = "unavailable",
}

export const emitTimeStepViewedEvent = ({
  viewId,
  apptReasonId,
  locationId,
  milesAway,
  slotOptionsId,
  specialtyId,
}: {
  viewId: string;
  apptReasonId?: string;
  slotOptionsId?: string;
  locationId: string;
  milesAway?: number;
  limitedAvailabilityFlag?: boolean;
  specialtyId: string;
}) => {
  postEvent({
    typeId: EventType.TimeStepViewed,
    extraData: {
      bookingStep: BookingStep.TIME_SELECTION,
      slotOptionsId,
      // initialApptOptions,
      viewId,
      flowId: getFlowId() || null,
      modality: {
        inPerson: ModalityState.DEFAULT,
        virtual: ModalityState.UNAVAILABLE,
      },
      apptDetails: {
        apptReasonId,
        specialtyId,
        locationId,
        milesAway,
      },
    },
  });
};

export const emitSlotClickedEvent = (
  slotId: string,
  slotTime: number,
  slotOptionsId: string,
  viewId: string,
) => {
  postEvent({
    typeId: EventType.SlotClicked,
    extraData: {
      slotOptionsId,
      value: slotId,
      time: slotTime,
      timeToAppt: slotTime ? slotTime - Date.now() : slotTime,
      viewId,
      flowId: getFlowId() || null,
    },
  });
};

export const sendApptConfirmedEvent = (apptDetails: ApptDetails, isRescheduling: boolean) =>
  queueEvent({
    typeId: EventType.AppointmentSchedulingConfirmed,
    extraData: {
      isReschedule: isRescheduling,
      category: CustomCategory.AppointmentScheduling,
      bookingStep: BookingStep.APPOINTMENT_CONFIRMATION,
      apptDetails,
      flowId: getFlowId(),
    },
  });

// Mostly taken from https://github.com/mdcollab/mdcollab/blob/master/frontend/apps/hib/src/utils/trackApptConfirmationGaEvent.ts
export const sendApptConfirmedGAEvent = (
  appt: {
    instant: boolean;
    virtual: boolean;
    apptSpecialty: {
      name: string;
    };
    /**
     * This fn doesn't care about the properties of the slot object, just whether or not it exists.
     */
    slot: unknown;
  },
  locationSlug = "",
  practiceId = "",
) => {
  const {instant, virtual, slot, apptSpecialty} = appt;

  if (instant) return;

  const label = virtual ? apptSpecialty.name : !slot ? "referral" : locationSlug;
  const practiceName = PracticeNameById[practiceId] || "Unknown Practice";

  GA({
    category: `${practiceName} Appointment`,
    action: "Confirmed",
    label,
  });
};
